import EdgeOrREST from '../EdgeOrREST';
import { buildTextDataList } from './reportDataFormatter';
import logoIcon from '../IMG/Logos/LR_Title_Logo.jpg';

const toBase64 = (blob) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));
const REPORT_CAPTURE_SCALE = 2.5;
const REPORT_CAPTURE_JPEG_QUALITY = 1;

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        if (!src) {
            resolve(null);
            return;
        }

        const image = new window.Image();
        image.onload = () => resolve(image);
        image.onerror = (error) => reject(error);
        image.src = src;
    });

const getRelativeRect = (containerRect, elementRect) => ({
    x: elementRect.left - containerRect.left,
    y: elementRect.top - containerRect.top,
    width: elementRect.width,
    height: elementRect.height,
});

const getLatestDxfCanvas = (dxfContainerRef) => {
    const dxfContainer = dxfContainerRef?.current;
    if (!dxfContainer) return null;

    const canvases = dxfContainer.querySelectorAll('canvas');
    if (!canvases.length) return null;

    return canvases[canvases.length - 1];
};

const buildPlanCompositeCanvas = async (stage, dxfContainerRef, dxfViewer) => {
    const stageContainer = stage?.container?.();
    const planContainer = stageContainer?.parentElement;
    if (!planContainer || !stageContainer) return null;

    const planRect = planContainer.getBoundingClientRect();
    const stageRect = stageContainer.getBoundingClientRect();
    if (!planRect.width || !planRect.height || !stageRect.width || !stageRect.height) return null;

    const compositeCanvas = document.createElement('canvas');
    compositeCanvas.width = Math.max(1, Math.round(planRect.width * REPORT_CAPTURE_SCALE));
    compositeCanvas.height = Math.max(1, Math.round(planRect.height * REPORT_CAPTURE_SCALE));

    const context = compositeCanvas.getContext('2d');
    context.scale(REPORT_CAPTURE_SCALE, REPORT_CAPTURE_SCALE);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, planRect.width, planRect.height);

    const dxfCanvas = dxfViewer?.renderer?.domElement || getLatestDxfCanvas(dxfContainerRef);
    if (dxfCanvas) {
        if (dxfViewer?.renderer && dxfViewer?.scene && dxfViewer?.camera) {
            dxfViewer.renderer.render(dxfViewer.scene, dxfViewer.camera);
        }

        const dxfRect = dxfCanvas.getBoundingClientRect();
        const dxfImage = await loadImage(dxfCanvas.toDataURL('image/png'));
        const relativeDxfRect = getRelativeRect(planRect, dxfRect);

        context.drawImage(
            dxfImage,
            relativeDxfRect.x,
            relativeDxfRect.y,
            relativeDxfRect.width,
            relativeDxfRect.height
        );
    }

    const stageImage = await loadImage(stage.toDataURL({ pixelRatio: REPORT_CAPTURE_SCALE }));
    const relativeStageRect = getRelativeRect(planRect, stageRect);

    context.drawImage(
        stageImage,
        relativeStageRect.x,
        relativeStageRect.y,
        relativeStageRect.width,
        relativeStageRect.height
    );

    return compositeCanvas;
};

const getNodeBoundsInPlanContainer = (stage, node, padding = 0) => {
    if (!stage || !node) return null;

    const planContainer = stage.container()?.parentElement;
    const stageContainer = stage.container?.();
    if (!planContainer || !stageContainer) return null;

    const planRect = planContainer.getBoundingClientRect();
    const stageRect = stageContainer.getBoundingClientRect();
    const nodeRect = node.getClientRect();

    return normalizeBounds({
        x: (stageRect.left - planRect.left) + nodeRect.x - padding,
        y: (stageRect.top - planRect.top) + nodeRect.y - padding,
        width: nodeRect.width + (padding * 2),
        height: nodeRect.height + (padding * 2),
    });
};

const normalizeBounds = (bounds) => {
    if (!bounds) return null;

    const normalized = { ...bounds };

    if (normalized.width < 0) {
        normalized.x += normalized.width;
        normalized.width = Math.abs(normalized.width);
    }

    if (normalized.height < 0) {
        normalized.y += normalized.height;
        normalized.height = Math.abs(normalized.height);
    }

    normalized.width = Math.max(1, normalized.width);
    normalized.height = Math.max(1, normalized.height);

    return normalized;
};

const getReportCaptureBounds = (stage, reportCaptureArea, padding = 0) => {
    if (!reportCaptureArea?.id) return null;

    const node = stage.findOne(`#report-capture-${reportCaptureArea.id}`);
    if (!node) return null;

    return getNodeBoundsInPlanContainer(stage, node, padding);
};

const findGridNode = (stage, grid) => {
    const byId = stage.findOne(`#grid-shape-${grid.id}`);
    if (byId) return byId;

    const byName = stage.findOne(`.grid-shape-${grid.id}`);
    if (byName) return byName;

    const byPredicate = stage.findOne((node) => {
        if (!node || typeof node.id !== 'function') return false;
        return node.id() === `grid-shape-${grid.id}`;
    });
    if (byPredicate) return byPredicate;

    return null;
};

const getGridVisualBounds = (stage, grid, gridScaleX, gridScaleY, dxfScale, padding = 50) => {
    const gridNode = findGridNode(stage, grid);
    if (gridNode) {
        return {
            bounds: getNodeBoundsInPlanContainer(stage, gridNode, padding),
            sourceType: 'grid-node',
        };
    }

    return {
        bounds: getFallbackGridBounds(stage, grid, gridScaleX, gridScaleY, dxfScale, padding),
        sourceType: 'grid-fallback',
    };
};

const getGridVisualBoundsOnly = (stage, grid, gridScaleX, gridScaleY, dxfScale, padding = 50) => {
    return getGridVisualBounds(stage, grid, gridScaleX, gridScaleY, dxfScale, padding).bounds;
};

const getRectIntersectionArea = (a, b) => {
    if (!a || !b) return 0;

    const left = Math.max(a.x, b.x);
    const top = Math.max(a.y, b.y);
    const right = Math.min(a.x + a.width, b.x + b.width);
    const bottom = Math.min(a.y + a.height, b.y + b.height);

    if (right <= left || bottom <= top) return 0;
    return (right - left) * (bottom - top);
};

const findBestReportCaptureArea = (stage, reportCaptureAreas, grid, gridScaleX, gridScaleY, dxfScale, usedIds) => {
    if (!Array.isArray(reportCaptureAreas) || reportCaptureAreas.length === 0) return null;

    const gridBounds = getGridVisualBoundsOnly(stage, grid, gridScaleX, gridScaleY, dxfScale, 0);
    let bestArea = null;
    let bestOverlap = 0;

    reportCaptureAreas.forEach((area) => {
        if (!area?.id || usedIds.has(area.id)) return;

        const areaBounds = getReportCaptureBounds(stage, area, 0);
        const overlap = getRectIntersectionArea(gridBounds, areaBounds);

        if (overlap > bestOverlap) {
            bestOverlap = overlap;
            bestArea = area;
        }
    });

    return bestArea;
};

const getFallbackGridBounds = (stage, grid, gridScaleX, gridScaleY, dxfScale = 1, padding = 50) => {
    const stageScaleX = stage.scaleX();
    const stageScaleY = stage.scaleY();
    const stageX = stage.x();
    const stageY = stage.y();

    const scaledGridX = grid.origin.x.start * gridScaleX * dxfScale;
    const scaledGridY = grid.origin.y.start * gridScaleY * dxfScale;
    const scaledGridWidth = grid.origin.x.length * gridScaleX * dxfScale;
    const scaledGridHeight = grid.origin.y.length * gridScaleY * dxfScale;

    const angle = (grid.angle || 0) * Math.PI / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    const aabbWidth = Math.abs(scaledGridWidth * cos) + Math.abs(scaledGridHeight * sin);
    const aabbHeight = Math.abs(scaledGridWidth * sin) + Math.abs(scaledGridHeight * cos);

    const x2 = scaledGridWidth * cos;
    const y2 = scaledGridWidth * sin;
    const x3 = scaledGridHeight * -sin;
    const y3 = scaledGridHeight * cos;
    const x4 = x2 + x3;
    const y4 = y2 + y3;

    const offsetX = Math.min(0, x2, x3, x4);
    const offsetY = Math.min(0, y2, y3, y4);

    const captureX = (scaledGridX + offsetX) * stageScaleX + stageX;
    const captureY = (scaledGridY + offsetY) * stageScaleY + stageY;
    const captureWidth = aabbWidth * stageScaleX;
    const captureHeight = aabbHeight * stageScaleY;

    return normalizeBounds({
        x: captureX - padding,
        y: captureY - padding,
        width: captureWidth + (padding * 2),
        height: captureHeight + (padding * 2),
    });
};
/**
 * Initiates the report capture process by preparing the UI. This is the main entry point.
 * @param {boolean} maskOn - True if masks should be included in the report.
 * @param {object} params - Object containing all necessary parameters.
 * @param {object} outdoorResults - Contains Eav, Emax, and Emin values.
 */
export const handleReportCaptureClick = (maskOn, params, outdoorResults) => {
    const { stageRef, reportCaptureAreas, setReportCaptureAreas, masks, setMasks } = params;

    console.log(params);

    if (stageRef.current) {
        let projectDetails = {
            designer: params.user?.userName || 'N/A',
            projectNo: params.projectNumber || 'N/A',
            projectName: params.projectName || 'N/A',
        };

        const textDataList = buildTextDataList(
            projectDetails,
            outdoorResults,
            params.user
        );

        console.log("Prepared Text Data List for Report Capture:", textDataList);

        const originalReportAreaStates = reportCaptureAreas.map(item => ({
            id: item.id,
            opacity: item.opacity,
        }));
        //The Custom area, not in use while now
        setReportCaptureAreas(reportCaptureAreas.map(item => ({
            ...item,
            opacity: 0,
            selected: false,
        })));

        let originalMasks = null;
        if (!maskOn) {
            originalMasks = masks.map(item => ({ id: item.id, opacity: item.opacity }));
            setMasks(masks.map(item => ({ ...item, opacity: 0, selected: false })));
        } else {
            setMasks(masks.map(item => ({ ...item, selected: false })));
        }

        // Delay capture to allow Konva to re-render with hidden items.
        // CORRECTED: The callback is now async to allow 'await' inside.
        setTimeout(async () => {
            await nextFrame();
            await captureReportAreas(
                { ...params, textDataList },
                originalReportAreaStates,
                originalMasks
            );
        }, 100); // A small delay like 100ms is safer than 0 to ensure render completion.
    }
};

/**
 * Captures report areas, assembles the DTO, and sends it to the backend.
 * @param {object} params - Object containing all necessary parameters.
 * @param {Array} originalStates - The original opacity states of report capture areas.
 * @param {Array|null} originalMasks - The original opacity states of masks.
 */
// CORRECTED: The function is now async to await the capture process.
export const captureReportAreas = async (params, originalStates, originalMasks) => {
    const {
        stageRef,
        reportCaptureAreas,
        setReportCaptureAreas,
        masks,
        setMasks,
        setSnack,
        textDataList,
        selectedTemplate,
        grids,
        areaResults,
        columns,
        gridScaleX,
        gridScaleY,
        dxfContainerRef,
        dxfViewer,
        fontSize,
        fontFamily,
        projectName,
        projectNumber,
        user,
        customLogo,
        setIsLoading,
        syncStageFromViewer,
    } = params;

    const stage = stageRef.current;
    if (!stage) {
        console.error("Capture process failed: Stage is not available.");
        return;
    }

    const originalScale = stage.scaleX();
    const originalPosition = { x: stage.x(), y: stage.y() };
    const originalSize = { width: stage.width(), height: stage.height() };

    try {
        const gridsForBackend = transformGridsForBackend(grids, areaResults);
        const layoutsForBackend = transformColumnsToLayouts(columns);

        const imageDataList = await CaptureAreaByGrids(
            grids,
            reportCaptureAreas,
            stageRef,
            gridScaleX,
            gridScaleY,
            params.dxfScale || 1,
            dxfContainerRef,
            dxfViewer,
            syncStageFromViewer
        );

        if (customLogo) {
            // If a custom logo is provided, use it directly.
            imageDataList.push({
                name: 'company-logo-main',
                data: customLogo,
            });
        } else {
            // 1. Fetch the static image file as a Blob
            const response = await fetch(logoIcon);
            const iconBlob = await response.blob();
            const iconBase64String = await toBase64(iconBlob);
            // 2. Add the logo to the imageDataList with the correct name
            imageDataList.push({
                name: 'company-logo-main',
                data: iconBase64String
            });
        }

        if (!imageDataList || imageDataList.length === 0) {
            console.error("Capture failed: No images were generated.");
            setSnack({ open: true, message: "Error: Failed to capture report images.", severity: 'error' });
            return; // Stop execution
        }

        console.log(`Successfully captured ${imageDataList.length} images.`);

        // CORRECTED: Assembled the DTO with the actual data. The 'Icon' property is no longer needed.
        const reportCaptureDTO = {
            elements: [],
            content: {
                textData: textDataList,
                imageData: imageDataList,
                Grids: gridsForBackend,
                Layouts: layoutsForBackend,
            },
            templateId: selectedTemplate.id,
            FontSize: fontSize,
            FontFamily: fontFamily,
            ProjectName: projectName,
            ProjectNumber: projectNumber,
        };

        if (params.viewTemplate) {
            EdgeOrREST(null, 'FETCH_REPORT_HTML', (response) => onFetchReportHTML(response, params), reportCaptureDTO);
        } else {
            setIsLoading(true);
            try {
                const response = await EdgeOrREST(
                    null,
                    'UPLOAD_REPORT_CAPTURE',
                    null,
                    reportCaptureDTO
                );

                onUploadReportCapture(response, setSnack);
            } catch (error) {
                setSnack({
                    open: true,
                    message: 'Report upload failed',
                    severity: 'error',
                });
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        }

    } catch (error) {
        console.error("An error occurred during the report capture process:", error);
        setSnack({ open: true, message: "An unexpected error occurred during capture.", severity: 'error' });
    } finally {
        // Restore UI state regardless of success or failure.
        stage.size(originalSize);
        stage.scale({ x: originalScale, y: originalScale });
        stage.position(originalPosition);

        setReportCaptureAreas(originalStates.map(state => {
            const item = reportCaptureAreas.find(area => area.id === state.id);
            return item ? { ...item, opacity: state.opacity } : null;
        }).filter(Boolean));

        if (originalMasks != null) {
            setMasks(originalMasks.map(state => {
                const item = masks.find(mask => mask.id === state.id);
                return item ? { ...item, opacity: state.opacity } : null;
            }).filter(Boolean));
        }

        requestAnimationFrame(() => {
            stage.getLayers?.().forEach((layer) => layer.batchDraw?.());
            stage.batchDraw?.();
            stage.draw?.();

            if (typeof syncStageFromViewer === 'function') {
                syncStageFromViewer();
                setTimeout(() => syncStageFromViewer(), 50);
            } else if (dxfViewer?.renderer && dxfViewer?.scene && dxfViewer?.camera) {
                dxfViewer.renderer.render(dxfViewer.scene, dxfViewer.camera);
            }
        });
    }
};

/**
 * Handles the logic for uploading captured report areas after the API responds.
 * @param {object} response - The API response object.
 * @param {function} setSnack - Function to set notification snackbar state.
 */
export const onUploadReportCapture = (response, setSnack) => {
    console.log("Upload Report Capture Response", response);
    if (response && response.status === 200 && response.data) {
        const link = document.createElement('a');
        link.download = 'report.pdf';
        link.href = response.data;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSnack({
            open: true,
            message: 'Report capture success',
            severity: 'success'
        });


    } else {
        console.error("Report capture failed. Received response:", response);
        setSnack({
            open: true,
            message: "Report capture failed",
            severity: 'error'
        });
    }
};

export const onFetchReportHTML = (response, params) => {
    params.setHtmlContent(response.data);
    params.setHtmlPreviewOpen(true);
};


/* ==========================================================================
   Internal Helper Functions 
   ========================================================================== */

// --- Image Capture Helpers ---

/**
 * Captures the visible plan area and crops each report image from that composite.
 */
const CaptureAreaByGrids = async (
    grids,
    reportCaptureAreas,
    stageRef,
    gridScaleX,
    gridScaleY,
    dxfScale = 1,
    dxfContainerRef,
    dxfViewer,
    syncStageFromViewer
) => {
    const stage = stageRef.current;
    if (!stage) return [];

    try {
        if (typeof syncStageFromViewer === 'function') {
            syncStageFromViewer();
        }

        await nextFrame();

        const compositeCanvas = await buildPlanCompositeCanvas(stage, dxfContainerRef, dxfViewer);
        if (!compositeCanvas) {
            console.error("Plan composite canvas could not be created for report capture.");
            return [];
        }

        console.log(grids);
        const usedReportCaptureIds = new Set();

        return grids.slice(1).map((grid, index) => {
            let reportCaptureArea = findBestReportCaptureArea(
                stage,
                reportCaptureAreas,
                grid,
                gridScaleX,
                gridScaleY,
                dxfScale,
                usedReportCaptureIds
            );

            if (!reportCaptureArea && Array.isArray(reportCaptureAreas)) {
                reportCaptureArea = reportCaptureAreas[index] || null;
            }

            if (reportCaptureArea?.id) {
                usedReportCaptureIds.add(reportCaptureArea.id);
            }

            const gridVisual = getGridVisualBounds(stage, grid, gridScaleX, gridScaleY, dxfScale);
            const bounds = getReportCaptureBounds(stage, reportCaptureArea) || gridVisual.bounds;
            const cropCanvas = document.createElement('canvas');
            cropCanvas.width = Math.max(1, Math.round(bounds.width * REPORT_CAPTURE_SCALE));
            cropCanvas.height = Math.max(1, Math.round(bounds.height * REPORT_CAPTURE_SCALE));

            const cropContext = cropCanvas.getContext('2d');
            cropContext.fillStyle = '#ffffff';
            cropContext.fillRect(0, 0, cropCanvas.width, cropCanvas.height);
            cropContext.drawImage(
                compositeCanvas,
                bounds.x * REPORT_CAPTURE_SCALE,
                bounds.y * REPORT_CAPTURE_SCALE,
                bounds.width * REPORT_CAPTURE_SCALE,
                bounds.height * REPORT_CAPTURE_SCALE,
                0,
                0,
                cropCanvas.width,
                cropCanvas.height
            );

            return {
                name: `${grid.name}_image`,
                data: cropCanvas.toDataURL('image/jpeg', REPORT_CAPTURE_JPEG_QUALITY)
            };
        });

    } catch (error) {
        console.error("An error occurred during the report capture process:", error);
        return [];
    }
};

// --- Data Transformation Helpers ---

/**
 * Transforms frontend grid data into the structure required by the backend.
 */
const transformGridsForBackend = (frontendGrids, areaResults) => {
    if (!frontendGrids || !areaResults) return [];

    return frontendGrids
        .filter(grid => grid.id !== 0)
        .map(grid => {
            const areaResultContainer = areaResults.find(item => {
                const resultObj = Array.isArray(item) ? item[0] : item;
                return String(resultObj?.calcResults?.outdoorResults?.[0]?.gridId) === String(grid.id);
            });

            const baseGrid = {
                Id: String(grid.id),
                GridName: grid.name,
                X: String(grid.origin.x.start),
                Y: String(grid.origin.y.start),
                XLength: String(grid.origin.x.length),
                YLength: String(grid.origin.y.length),
                XSpacing: String(grid.xSpacing),
                YSpacing: String(grid.ySpacing),
                GridClass: String(grid.gridClass),
                GridStandard: String(grid.gridStandard),
            };

            if (!areaResultContainer) {
                return baseGrid;
            }

            const finalResultData = Array.isArray(areaResultContainer) ? areaResultContainer[0] : areaResultContainer;
            const outdoorResult = finalResultData.calcResults.outdoorResults[0];

            // Safely calculate ratios, defaulting to "0" if denominator is 0
            const eminEmax = Number(outdoorResult.emax ? String(outdoorResult.emin / outdoorResult.emax) : "0");
            const eminEav = Number(outdoorResult.eav ? String(outdoorResult.emin / outdoorResult.eav) : "0");

            return {
                ...baseGrid,
                Eav: String(outdoorResult.eav),
                Emin: String(outdoorResult.emin),
                Emax: String(outdoorResult.emax),
                EminEmax: eminEmax,
                EminEav: eminEav,
            };
        });
};

/**
 * Transforms frontend column data into the "layout" structure required by the backend.
 */
const transformColumnsToLayouts = (columns) => {
    if (!columns || !Array.isArray(columns)) {
        return [];
    }

    return columns.map(col => ({
        Id: col.name,
        Type: 'Column',
        X: col.values.point.x.toString(),
        Y: col.values.point.y.toString(),
        Height: col.values.height.toString(),
        Angle: col.values.positionAngle.toString(),
        Tilt: col.values.tilt.toString(),
        Cant: col.values.cant.toString(),
        Outreach: col.values.outreach.toString(),
        TargetX: '',
        TargetY: '',
        TargetZ: '',
        luminaireId: col.values.luminaireId
    }));
};
